// Flow types automatically removed

/* eslint-disable import/no-default-export */
import { Component } from 'react';
import DynamicInput from 'dibs-dynamic-input';
import { defineMessages, FormattedMessage, IntlContext } from 'dibs-react-intl';
import debounce from 'lodash/debounce.js';

const SHOW_CUSTOM_QUANTITY = 31;
const INPUT_SIZE = 'large';
const NON_NUMERIC_REGEX = /([^\d])/;

const getValOrDefault = (val, defaultVal) => {
    return typeof val === 'undefined' || val === null ? defaultVal : val;
};

const messages = defineMessages({
    customTextPlaceholder: {
        defaultMessage: 'Enter custom quantity',
        id: 'dc.DynamicQuantitySelect.customTextPlaceholder',
    },
    placeholder: {
        defaultMessage: 'Quantity',
        id: 'dc.DynamicQuantitySelect.placeholder',
    },
});

export class DynamicQuantitySelect extends Component {
    constructor(props) {
        super(props);
        const selectedQuantity = props.value;
        const showCustomInputQuantity = getValOrDefault(
            props.customInputQuantity,
            SHOW_CUSTOM_QUANTITY
        );
        this.onChange = this.onChange.bind(this);
        this.debouncedOnChange = props.onChange ? debounce(props.onChange, 200) : () => {};
        this.state = {
            renderTextInput: getValOrDefault(
                props.renderTextInput,
                Number(selectedQuantity) >= showCustomInputQuantity
            ),
            selectedQuantity,
            showCustomInputQuantity,
        };
    }

    onChange(e) {
        let value;

        if (typeof e === 'number') {
            value = String(e);
        } else {
            value = e?.currentTarget?.value;
        }

        const { onChange } = this.props;
        const renderTextInput =
            this.state.renderTextInput || Number(value) === this.state.showCustomInputQuantity; // Reset selected quantity when changing from dropdown -> text

        const shouldResetSelectedQuantity = !this.state.renderTextInput && renderTextInput;
        this.setState({
            renderTextInput,
            selectedQuantity: shouldResetSelectedQuantity || typeof value !== 'string' ? '' : value,
        });

        if (onChange && !this.getErrorMessageProp()) {
            // Reset quantity to 0 if input is being changed
            if (shouldResetSelectedQuantity) {
                value = 0;
            }

            if (renderTextInput) {
                this.debouncedOnChange(value);
            } else {
                onChange(value);
            }
        }
    }

    selectedQuantityIsNonNumeric() {
        return NON_NUMERIC_REGEX.test(this.state.selectedQuantity);
    }

    selectedQuantityIsTooHigh() {
        const { availableQuantity } = this.props;
        const { selectedQuantity } = this.state;
        return !!availableQuantity && Number(selectedQuantity) > availableQuantity;
    }

    selectedQuantityOutOfSanityRange() {
        return !!this.state.selectedQuantity && this.state.selectedQuantity > 500000;
    }

    getValidationErrors() {
        const errors = [];
        const { availableQuantity } = this.props;

        if (this.selectedQuantityIsNonNumeric() || this.selectedQuantityOutOfSanityRange()) {
            errors.push(
                <FormattedMessage
                    id="dc.DynamicQuantitySelect.errorNonNumericValue"
                    defaultMessage="Please enter a valid quantity"
                />
            );
        }

        if (this.selectedQuantityIsTooHigh()) {
            errors.push(
                <FormattedMessage
                    id="dc.DynamicQuantitySelect.errorQuantityTooHigh"
                    defaultMessage="Only {availableQuantity} available"
                    values={{
                        availableQuantity,
                    }}
                />
            );
        }

        return errors;
    }

    getErrorMessageProp() {
        const validationErrors = this.getValidationErrors();
        return validationErrors.length
            ? {
                  errorMessage: validationErrors[0],
              }
            : null;
    }

    getRightDecorator() {
        const { rightDecorator, soldAsSet } = this.props;
        const { selectedQuantity } = this.state;

        if (rightDecorator) {
            return rightDecorator;
        }

        return soldAsSet ? (
            <FormattedMessage
                id="dc.DynamicQuantitySelect.setsLanguage"
                defaultMessage="{quantity, plural, one {set} other {sets}}"
                values={{
                    quantity: selectedQuantity,
                }}
            />
        ) : (
            ''
        );
    }

    render() {
        const { renderTextInput, selectedQuantity } = this.state;
        const { dataTn, disabled, hasAnimatedPlaceholder, label, leftDecorator, options, size } =
            this.props;
        return (
            <DynamicInput
                {...this.getErrorMessageProp()}
                dataTn={dataTn}
                hasAnimatedPlaceholder={hasAnimatedPlaceholder}
                inputType={renderTextInput ? 'number' : 'select'}
                disabled={getValOrDefault(disabled, false)}
                label={label}
                leftDecorator={leftDecorator}
                placeholder={
                    renderTextInput
                        ? this.context.formatMessage(messages.customTextPlaceholder)
                        : this.context.formatMessage(messages.placeholder)
                }
                onChange={this.onChange}
                value={selectedQuantity}
                options={options}
                rightDecorator={this.getRightDecorator()}
                size={getValOrDefault(size, INPUT_SIZE)}
            />
        );
    }
}
DynamicQuantitySelect.contextType = IntlContext;
export default DynamicQuantitySelect;
